/* eslint-disable react/no-unescaped-entities */
/**
 * VoiceLibraryPage · /voice-library
 *
 * A standalone, calm space where the user meets every line they have
 * recorded in their own voice. This is the true user_voice_bank — the
 * one Soulumo reuses across future installations — not the older
 * per-journey voice-clips flow.
 *
 * Feb 24 2026 · Phase 4 refinement:
 *   · Search by text (client-side, instant)
 *   · Filter by theme (derived from where each line is used)
 *   · Filter by section (truth / trust / identity …)
 *   · Tap-to-play (a single audio element — never two lines at once)
 *   · Delete (with a soft confirm step)
 *   · "Used in these installations" expander per line — deep-links to
 *     the journey (private to the current user, of course)
 */
import { useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Search, Mic, MicOff, Play, Pause, Trash2, ChevronDown, ChevronRight, X, Loader2, Lock, ArrowLeft, RefreshCw, Sparkles, Square, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// Soulumo palette — matches AskAiJourneyModal for continuity.
const TOK = {
  pageBg: "linear-gradient(180deg, #0d1f3a 0%, #08172d 100%)",
  ink: "#0d1f3a",
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  creamMist: "rgba(245,224,173,0.45)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
  goldGlow: "rgba(231,201,138,0.16)",
  danger: "#dc8b6c",
};

const SECTION_LABELS = {
  truth: "Truth",
  nothing_missing: "Nothing Missing",
  trust: "Trust",
  identity: "Identity",
  performance: "Performance",
  final_loop: "Final Loop",
};

// A calm mapping so the filter chips don't read like machine ids.
const THEME_LABELS = {
  golf: "Golf",
  performance: "Performance",
  focus: "Focus",
  confidence: "Confidence",
  discipline: "Discipline",
  gym: "Gym",
  public_speaking: "Public Speaking",
  leadership: "Leadership",
  creative_flow: "Creative Flow",
  creativity: "Creativity",
  spanish: "Language",
  morning: "Morning",
  calm: "Calm",
  peace: "Peace",
  presence: "Presence",
  self_worth: "Self-Worth",
  self_compassion: "Self-Compassion",
  grounding: "Grounding",
  inner_belief: "Inner Belief",
  purpose: "Purpose",
  abundance: "Abundance",
  wealth: "Wealth",
  manifestation: "Manifestation",
  health: "Health",
  relationships: "Relationships",
  deep_sleep: "Deep Sleep",
  recovery: "Recovery",
  magnetism: "Magnetism",
  intimacy: "Intimacy",
};
const themeLabel = (id) => THEME_LABELS[id] || (id || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());


export default function VoiceLibraryPage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [broken, setBroken] = useState([]); // Recovery Nudge — hidden-broken rows
  const [usedIn, setUsedIn] = useState({}); // { text_hash: [{name, section, theme_id, journey_id, ...}] }

  // Filters + search
  const [q, setQ] = useState("");
  const [themeFilter, setThemeFilter] = useState("all");     // "all" | "<theme_id>" | "unused"
  const [sectionFilter, setSectionFilter] = useState("all"); // "all" | "<section>"

  // Playback state — single audio element for whole page
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingPlayId, setLoadingPlayId] = useState(null);
  const [playError, setPlayError] = useState(null);
  // Cache blob URLs so tapping the same row twice doesn't refetch.
  const blobCacheRef = useRef({}); // { entryId: objectUrl }

  // Expanded rows (used-in expander)
  const [expanded, setExpanded] = useState({}); // { id: true }

  // Delete confirm state
  const [confirmingId, setConfirmingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, usedRes, brokenRes] = await Promise.all([
        axios.get(`${API}/api/user-voice-bank`, auth()),
        axios.get(`${API}/api/user-voice-bank/used-in`, auth()),
        axios.get(`${API}/api/user-voice-bank/broken`, auth()),
      ]);
      setItems(listRes.data?.items || []);
      setUsedIn(usedRes.data?.map || {});
      setBroken(brokenRes.data?.items || []);
    } catch (e) {
      console.error("[voice-library] load failed:", e);
      setError("Couldn't load your voice library. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Derive available themes + sections from usedIn map ───────────
  const { availableThemes, availableSections, itemThemesById, itemSectionsById } = useMemo(() => {
    const themes = new Set();
    const sections = new Set();
    const itThemes = {}; // id → Set(theme_id)
    const itSections = {}; // id → Set(section)
    for (const it of items) {
      const usages = usedIn[it.text_hash] || [];
      const t = new Set(), s = new Set();
      for (const u of usages) {
        if (u.theme_id) { themes.add(u.theme_id); t.add(u.theme_id); }
        if (u.section) { sections.add(u.section); s.add(u.section); }
      }
      itThemes[it.id] = t;
      itSections[it.id] = s;
    }
    return {
      availableThemes: Array.from(themes).sort(),
      availableSections: Array.from(sections).sort(),
      itemThemesById: itThemes,
      itemSectionsById: itSections,
    };
  }, [items, usedIn]);

  // ── Filter + search pipeline ─────────────────────────────────────
  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return items.filter((it) => {
      if (qn && !(it.text || "").toLowerCase().includes(qn)) return false;
      const themes = itemThemesById[it.id] || new Set();
      const sections = itemSectionsById[it.id] || new Set();
      if (themeFilter === "unused") {
        if (themes.size > 0) return false;
      } else if (themeFilter !== "all") {
        if (!themes.has(themeFilter)) return false;
      }
      if (sectionFilter !== "all" && !sections.has(sectionFilter)) return false;
      return true;
    });
  }, [items, q, themeFilter, sectionFilter, itemThemesById, itemSectionsById]);

  // ── Play / pause single-track ───────────────────────────────────
  // The audio endpoint requires a Bearer token, but a raw <audio src>
  // request can't carry custom headers — so we fetch the clip as a
  // blob first, cache the resulting object URL, and hand THAT to the
  // audio element. Range-friendly URLs would work too but blob keeps
  // this page dead-simple and free of any auth workarounds.
  const togglePlay = async (item) => {
    const a = audioRef.current;
    if (!a) return;
    setPlayError(null);
    if (playingId === item.id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    try {
      let src = blobCacheRef.current[item.id];
      if (!src) {
        setLoadingPlayId(item.id);
        const r = await axios.get(`${API}${item.audio_url}`, {
          ...auth(),
          responseType: "blob",
        });
        src = URL.createObjectURL(r.data);
        blobCacheRef.current[item.id] = src;
      }
      a.src = src;
      a.currentTime = 0;
      await a.play();
      setPlayingId(item.id);
    } catch (e) {
      console.error("[voice-library] play failed:", e);
      setPlayError("Couldn't play that recording. Please try again.");
      setPlayingId(null);
    } finally {
      setLoadingPlayId(null);
    }
  };
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => setPlayingId(null);
    a.addEventListener("ended", onEnd);
    return () => a.removeEventListener("ended", onEnd);
  }, []);
  // Release blob URLs when the component unmounts.
  useEffect(() => () => {
    for (const url of Object.values(blobCacheRef.current)) {
      try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
    }
    blobCacheRef.current = {};
  }, []);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/user-voice-bank/${id}`, auth());
      setItems((prev) => prev.filter((x) => x.id !== id));
      if (playingId === id) {
        audioRef.current?.pause();
        setPlayingId(null);
      }
    } catch (e) {
      console.error("[voice-library] delete failed:", e);
      setPlayError("Couldn't remove that recording. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  const total = items.length;
  const showEmpty = !loading && !error && total === 0;
  const showFilteredEmpty = !loading && !error && total > 0 && filtered.length === 0;

  return (
    <div className="min-h-screen" style={{ background: TOK.pageBg }} data-testid="voice-library-page">
      {/* Hidden audio element used for all playback. */}
      <audio ref={audioRef} preload="none" />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 pb-24">
        {/* Header row */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => nav("/my-installations")}
            data-testid="voice-library-back"
            className="inline-flex items-center gap-2 text-[0.66rem] uppercase tracking-[0.28em]"
            style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
          >
            <ArrowLeft size={13} /> Back
          </button>
          <div className="uppercase tracking-[0.4em] text-[0.55rem]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            Soulumo · Private
          </div>
        </div>

        <h1
          className="font-light leading-[1.05]"
          style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "2.6rem", color: TOK.cream }}
          data-testid="voice-library-title"
        >
          Your Voice Library
        </h1>
        <p
          className="mt-3 italic max-w-lg leading-relaxed"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1.06rem" }}
        >
          Every line you have spoken in your own voice — searchable, sortable, replayable. Nothing here is shared. Nothing here leaves you.
        </p>

        {/* Recovery Nudge — warmly surface hidden-broken rows.
            Suppressed when the member has no healthy recordings at all
            (they haven't started yet — showing a "recover" strip would
            be confusing). */}
        {broken.length > 0 && (
          <RecoveryStrip
            broken={broken}
            onRecovered={(recoveredId, freshRow) => {
              // Give the RecoveryRow ~1.8s to show its "Restored in
              // your voice." confirmation before it disappears from
              // the strip.
              setTimeout(() => {
                setBroken((prev) => prev.filter((b) => b.id !== recoveredId));
              }, 1800);
              if (freshRow?.id) {
                setItems((prev) => [freshRow, ...prev.filter((x) => x.text_hash !== freshRow.text_hash)]);
              }
            }}
          />
        )}

        {/* Search + filter bar */}
        <div className="mt-7 flex flex-col gap-3" data-testid="voice-library-controls">
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl"
            style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
          >
            <Search size={14} style={{ color: TOK.goldSoft }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your recorded lines…"
              data-testid="voice-library-search"
              className="flex-1 bg-transparent outline-none"
              style={{ color: TOK.cream, fontFamily: '"Manrope", sans-serif', fontSize: "0.94rem" }}
            />
            {q && (
              <button
                onClick={() => setQ("")}
                data-testid="voice-library-search-clear"
                aria-label="Clear search"
                style={{ color: TOK.creamMist }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Theme chips */}
          {(availableThemes.length > 0 || items.length > 0) && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="voice-library-theme-filter">
              <FilterChip label="All themes" active={themeFilter === "all"} onClick={() => setThemeFilter("all")} testId="voice-library-theme-all" />
              {availableThemes.map((t) => (
                <FilterChip key={t} label={themeLabel(t)} active={themeFilter === t} onClick={() => setThemeFilter(t)} testId={`voice-library-theme-${t}`} />
              ))}
              <FilterChip label="Unused" active={themeFilter === "unused"} onClick={() => setThemeFilter("unused")} testId="voice-library-theme-unused" />
            </div>
          )}

          {/* Section chips */}
          {availableSections.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="voice-library-section-filter">
              <FilterChip label="All sections" active={sectionFilter === "all"} onClick={() => setSectionFilter("all")} testId="voice-library-section-all" small />
              {availableSections.map((s) => (
                <FilterChip key={s} label={SECTION_LABELS[s] || s} active={sectionFilter === s} onClick={() => setSectionFilter(s)} testId={`voice-library-section-${s}`} small />
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        {loading && (
          <div className="mt-10 flex items-center gap-2 text-sm italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }} data-testid="voice-library-loading">
            <Loader2 size={16} className="animate-spin" /> Gathering your recordings…
          </div>
        )}
        {error && (
          <div className="mt-8 px-4 py-3 rounded-xl text-sm italic"
               style={{ background: "rgba(216,80,80,0.10)", border: "1px solid rgba(216,80,80,0.40)", color: "#f0b4b4" }}
               data-testid="voice-library-error">
            {error}
          </div>
        )}

        {showEmpty && (
          <div
            className="mt-12 px-6 py-10 rounded-2xl text-center"
            style={{ background: "rgba(215,187,122,0.05)", border: `1px solid ${TOK.goldMist}` }}
            data-testid="voice-library-empty"
          >
            <Mic size={28} className="mx-auto" style={{ color: TOK.goldSoft }} />
            <p
              className="mt-4 italic"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.08rem" }}
            >
              You haven't recorded a line yet.
            </p>
            <p
              className="mt-2 text-xs italic max-w-sm mx-auto leading-relaxed"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
            >
              Build a journey and tap the microphone on any line you are ready to speak in your own voice. Your subconscious knows the sound of you better than any other voice in the world.
            </p>
          </div>
        )}

        {showFilteredEmpty && (
          <div
            className="mt-10 px-5 py-6 rounded-2xl text-center"
            style={{ background: "rgba(215,187,122,0.04)", border: `1px solid ${TOK.goldMist}` }}
            data-testid="voice-library-empty-filtered"
          >
            <p style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>
              No recordings match those filters.
            </p>
            <button
              onClick={() => { setQ(""); setThemeFilter("all"); setSectionFilter("all"); }}
              data-testid="voice-library-reset-filters"
              className="mt-2 text-[0.66rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
            >
              Clear filters
            </button>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <>
            {playError && (
              <div
                className="mt-6 px-4 py-3 rounded-xl text-sm italic"
                style={{ background: "rgba(216,80,80,0.10)", border: "1px solid rgba(216,80,80,0.40)", color: "#f0b4b4" }}
                data-testid="voice-library-play-error"
              >
                {playError}
              </div>
            )}
            <p
              className="mt-8 mb-3 text-[0.6rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
              data-testid="voice-library-count"
            >
              {filtered.length} of {total} recording{total === 1 ? "" : "s"}
            </p>

            <div className="space-y-2.5" data-testid="voice-library-list">
              {filtered.map((it) => {
                const usages = usedIn[it.text_hash] || [];
                const isExpanded = !!expanded[it.id];
                const isPlaying = playingId === it.id;
                const isLoading = loadingPlayId === it.id;
                const isConfirming = confirmingId === it.id;
                return (
                  <div
                    key={it.id}
                    className="px-4 py-3 rounded-xl"
                    style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
                    data-testid={`voice-library-row-${it.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Play / Pause */}
                      <button
                        onClick={() => togglePlay(it)}
                        disabled={isLoading}
                        data-testid={`voice-library-play-${it.id}`}
                        aria-label={isPlaying ? "Pause" : "Play"}
                        className="shrink-0 w-11 h-11 rounded-full inline-flex items-center justify-center transition-all"
                        style={{
                          background: isPlaying ? TOK.gold : "transparent",
                          border: `1px solid ${TOK.gold}`,
                          color: isPlaying ? TOK.ink : TOK.gold,
                          opacity: isLoading ? 0.7 : 1,
                        }}
                      >
                        {isLoading ? <Loader2 size={15} className="animate-spin" /> : isPlaying ? <Pause size={16} /> : <Play size={16} />}
                      </button>

                      {/* Text + meta */}
                      <div className="flex-1 min-w-0">
                        <p
                          className="leading-snug"
                          style={{
                            fontFamily: '"Cormorant Garamond", serif',
                            color: TOK.cream,
                            fontSize: "1.1rem",
                          }}
                          data-testid={`voice-library-text-${it.id}`}
                        >
                          {it.text}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[0.66rem]" style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}>
                          {usages.length > 0 ? (
                            <button
                              onClick={() => setExpanded((p) => ({ ...p, [it.id]: !p[it.id] }))}
                              data-testid={`voice-library-usedin-toggle-${it.id}`}
                              className="inline-flex items-center gap-1"
                              style={{ color: TOK.gold }}
                            >
                              {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              Used in {usages.length} installation{usages.length === 1 ? "" : "s"}
                            </button>
                          ) : (
                            <span style={{ color: TOK.creamMist }}>Not yet in an installation</span>
                          )}
                          {it.created_at && (
                            <span>· recorded {formatRelative(it.created_at)}</span>
                          )}
                        </div>
                      </div>

                      {/* Delete */}
                      <div className="shrink-0">
                        {isConfirming ? (
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={() => remove(it.id)}
                              data-testid={`voice-library-delete-confirm-${it.id}`}
                              className="text-[0.62rem] uppercase tracking-[0.22em] px-2.5 py-1.5 rounded-full"
                              style={{ background: "rgba(220,139,108,0.18)", border: `1px solid ${TOK.danger}`, color: TOK.danger, fontFamily: '"Manrope", sans-serif' }}
                            >
                              Remove
                            </button>
                            <button
                              onClick={() => setConfirmingId(null)}
                              data-testid={`voice-library-delete-cancel-${it.id}`}
                              className="text-[0.62rem] uppercase tracking-[0.22em]"
                              style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
                            >
                              Keep
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmingId(it.id)}
                            data-testid={`voice-library-delete-${it.id}`}
                            aria-label="Delete recording"
                            className="opacity-70 hover:opacity-100 transition-opacity inline-flex items-center justify-center"
                            style={{ color: TOK.creamMist, width: 44, height: 44, margin: -8 }}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Used-in expander */}
                    {isExpanded && usages.length > 0 && (
                      <div
                        className="mt-3 pt-3 pl-12 space-y-1.5"
                        style={{ borderTop: `1px solid ${TOK.goldMist}` }}
                        data-testid={`voice-library-usedin-${it.id}`}
                      >
                        {usages.map((u, i) => (
                          <button
                            key={`${u.journey_id}-${u.section}-${i}`}
                            onClick={() => nav(`/my-journeys?jid=${encodeURIComponent(u.journey_id || "")}`)}
                            data-testid={`voice-library-usedin-item-${it.id}-${i}`}
                            className="w-full text-left flex items-center gap-2 text-[0.78rem] py-1 hover:opacity-90 transition-opacity"
                            style={{ color: TOK.cream, fontFamily: '"Manrope", sans-serif' }}
                          >
                            <ChevronRight size={11} style={{ color: TOK.goldSoft }} />
                            <span className="flex-1 truncate">{u.name || "Untitled installation"}</span>
                            <span className="text-[0.62rem]" style={{ color: TOK.goldSoft }}>{SECTION_LABELS[u.section] || u.section}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Privacy footer — tap to read the full promise */}
            <button
              type="button"
              onClick={() => nav("/voice-library/privacy")}
              className="mt-8 w-full flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl text-left transition-all"
              style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
              data-testid="voice-library-privacy-note"
            >
              <Lock size={12} style={{ color: TOK.gold, marginTop: 2 }} />
              <div className="text-[0.72rem] flex-1" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.94rem" }}>
                <span style={{ color: TOK.gold }}>Private to you.</span>{" "}
                Every recording is stored under your account and only served to you. Nothing here is shared with other users or added to the library.
                <span className="block mt-1 text-[0.62rem] uppercase tracking-[0.24em]" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
                  Read the full promise →
                </span>
              </div>
            </button>
          </>
        )}
      </div>
    </div>
  );
}


function FilterChip({ label, active, onClick, testId, small = false }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center rounded-full transition-all ${small ? "px-2.5 py-1 text-[0.6rem]" : "px-3 py-1.5 text-[0.66rem]"}`}
      style={{
        background: active ? TOK.goldGlow : "transparent",
        border: `1px solid ${active ? TOK.gold : TOK.goldMist}`,
        color: active ? TOK.gold : TOK.creamMist,
        fontFamily: '"Manrope", sans-serif',
        textTransform: "uppercase",
        letterSpacing: "0.2em",
      }}
    >
      {label}
    </button>
  );
}


function formatRelative(iso) {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(1, Math.floor((now - then) / 1000));
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hr ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} day${d === 1 ? "" : "s"} ago`;
    const w = Math.floor(d / 7);
    if (w < 5) return `${w} week${w === 1 ? "" : "s"} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
    const y = Math.floor(d / 365);
    return `${y} yr${y === 1 ? "" : "s"} ago`;
  } catch {
    return "";
  }
}



// ── Recovery Nudge ──────────────────────────────────────────────
// Warm strip that surfaces hidden-broken recordings (rows the health
// check has marked `is_broken=True`). The words are still yours;
// only the audio needs speaking again. Each row has its own
// mini-recorder that POSTs the fresh bytes back to
// /api/user-voice-bank/record — the backend upserts by (user, text)
// so the broken doc is atomically replaced by a healthy one.
function RecoveryStrip({ broken, onRecovered }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div
      className="mt-6 rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(231,201,138,0.10), rgba(231,201,138,0.04))",
        border: `1px solid ${TOK.gold}`,
      }}
      data-testid="voice-library-recovery-strip"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="voice-library-recovery-toggle"
        aria-expanded={expanded}
        aria-controls="voice-library-recovery-list"
        className="w-full flex items-start gap-3 px-4 py-3.5 text-left"
      >
        <div
          className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center"
          style={{ background: TOK.goldGlow, border: `1px solid ${TOK.gold}`, color: TOK.gold }}
          aria-hidden
        >
          <RefreshCw size={14} />
        </div>
        <div className="flex-1">
          <div
            className="uppercase tracking-[0.24em] text-[0.6rem]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            Recover your voice
          </div>
          <div
            className="mt-1"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.14rem", lineHeight: 1.25 }}
          >
            {broken.length} recording{broken.length === 1 ? "" : "s"} need{broken.length === 1 ? "s" : ""} re-recording{broken.some((b) => b.broken_reason === "silent") ? " (some came through silent)" : ""}.
          </div>
          <div
            className="mt-1 italic text-[0.9rem]"
            style={{ color: TOK.cream, opacity: 0.85, fontFamily: '"Cormorant Garamond", serif' }}
          >
            The words are still yours — only the audio needs speaking again. Tap a line, breathe, and let your voice carry it.
          </div>
        </div>
        <div className="shrink-0 mt-1" style={{ color: TOK.gold }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {expanded && (
        <div
          id="voice-library-recovery-list"
          className="px-4 pb-4 space-y-2"
          style={{ borderTop: `1px solid ${TOK.goldMist}` }}
          data-testid="voice-library-recovery-list"
        >
          <div className="h-2" />
          {broken.map((row) => (
            <RecoveryRow key={row.id} row={row} onRecovered={onRecovered} />
          ))}
        </div>
      )}
    </div>
  );
}


function RecoveryRow({ row, onRecovered }) {
  const [state, setState] = React.useState("idle"); // idle | recording | uploading | done | error
  const [errMsg, setErrMsg] = React.useState(null);
  const recRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const streamRef = React.useRef(null);

  const cleanupStream = () => {
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
  };
  React.useEffect(() => cleanupStream, []);

  const start = async () => {
    setErrMsg(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrMsg("Your browser can't reach the microphone.");
      setState("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => upload();
      rec.start();
      setState("recording");
    } catch (e) {
      console.error("[recovery-row] mic denied:", e);
      setErrMsg("Microphone permission is needed to re-record this line.");
      setState("error");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
  };

  const upload = async () => {
    setState("uploading");
    try {
      const type = recRef.current?.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      const fd = new FormData();
      fd.append("text", row.text);
      fd.append("audio", blob, type.includes("mp4") ? "recording.m4a" : "recording.webm");
      const r = await axios.post(`${API}/api/user-voice-bank/record`, fd, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      cleanupStream();
      setState("done");
      onRecovered?.(row.id, r.data);
    } catch (e) {
      console.error("[recovery-row] upload failed:", e);
      const detail = e?.response?.data?.detail;
      setErrMsg(
        detail === "audio_too_small" || detail === "audio_empty_bytes" || detail === "audio_silent"
          ? "I couldn't hear anything. Try again a little closer to the phone."
          : detail === "audio_format_unrecognised"
            ? "Couldn't read that recording. Please try once more."
            : "Something got in the way. Please try again."
      );
      cleanupStream();
      setState("error");
    }
  };

  const isDone = state === "done";
  const isRecording = state === "recording";
  const isUploading = state === "uploading";

  return (
    <div
      className="flex items-start gap-3 px-3 py-2.5 rounded-xl"
      style={{
        background: isDone ? "rgba(215,187,122,0.12)" : "rgba(245,236,217,0.03)",
        border: `1px solid ${isDone ? TOK.gold : TOK.goldMist}`,
      }}
      data-testid={`voice-library-recovery-row-${row.id}`}
    >
      <div className="flex-1 min-w-0">
        <p style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.06rem", lineHeight: 1.35 }}>
          {row.text}
          {row.broken_reason === "silent" && <span className="ml-2 align-middle text-[0.55rem] uppercase tracking-[0.2em] rounded-full px-2 py-0.5" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }} data-testid={`voice-library-recovery-silent-${row.id}`}>came through silent</span>}
        </p>
        {errMsg && (
          <p
            className="mt-1 text-[0.74rem] italic"
            style={{ color: "#f0b4b4", fontFamily: '"Cormorant Garamond", serif' }}
            data-testid={`voice-library-recovery-error-${row.id}`}
          >
            {errMsg}
          </p>
        )}
        {isDone && (
          <p
            className="mt-1 text-[0.72rem] italic inline-flex items-center gap-1"
            style={{ color: TOK.gold, fontFamily: '"Cormorant Garamond", serif' }}
          >
            <Sparkles size={10} /> Restored in your voice.
          </p>
        )}
      </div>

      {isDone ? (
        <div className="shrink-0 mt-1" style={{ color: TOK.gold }} aria-label="Recovered"><Check size={16} /></div>
      ) : isUploading ? (
        <button
          disabled
          className="shrink-0 mt-0.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: "rgba(215,187,122,0.16)", border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: '"Manrope", sans-serif', fontSize: "0.62rem", letterSpacing: "0.24em", textTransform: "uppercase" }}
          data-testid={`voice-library-recovery-uploading-${row.id}`}
        >
          <Loader2 size={11} className="animate-spin" /> Saving
        </button>
      ) : isRecording ? (
        <button
          onClick={stop}
          className="shrink-0 mt-0.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: TOK.gold, color: TOK.ink, fontFamily: '"Manrope", sans-serif', fontSize: "0.62rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600 }}
          data-testid={`voice-library-recovery-stop-${row.id}`}
        >
          <Square size={10} fill="currentColor" /> Stop
        </button>
      ) : (
        <button
          onClick={start}
          className="shrink-0 mt-0.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all"
          style={{
            background: state === "error" ? "rgba(220,139,108,0.10)" : "transparent",
            border: `1px solid ${state === "error" ? TOK.gold : TOK.goldMist}`,
            color: TOK.gold,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem",
            letterSpacing: "0.24em",
            textTransform: "uppercase",
          }}
          data-testid={`voice-library-recovery-record-${row.id}`}
        >
          {state === "error" ? <MicOff size={11} /> : <Mic size={11} />}
          {state === "error" ? "Try again" : "Re-record"}
        </button>
      )}
    </div>
  );
}
